import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import checkLoginStatusAPI from '../API/checkLoginStatus';
import getDashboardInfo from '../API/getDashboardInfo';
import subscribeToNewsletter from '../API/NewsLetter';
import unsubscribeFromNewsletter from '../API/unsubscribeAPI';
import deleteAccount from '../API/deleteAccountAPI';
import updateEmail from '../API/updateEmailAPI';
import updatePassword from '../API/updatePasswordAPI';
import './Dashboard.css';

// Utility functions for client-side validation
const validatePassword = (password) => {
  if (password.length < 8) return 'Password must be at least 8 characters long.';
  if (!/\d/.test(password)) return 'Password must contain at least one number.';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character.';
  return null;
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? null : 'Invalid email format.';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { updateLoginStatus } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const loginStatus = await checkLoginStatusAPI();
        if (loginStatus.isLoggedIn) {
          const dashboardInfo = await getDashboardInfo();
          if (dashboardInfo.success) {
            setEmail(dashboardInfo.email);
            setIsSubscribed(dashboardInfo.isSubscribed);
          } else {
            console.error('Failed to fetch dashboard info');
          }
        } else {
          navigate('/login', { state: { from: '/dashboard' } });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        navigate('/login', { state: { from: '/dashboard' } });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  const handleUpdatePassword = async () => {
    // Client-side password validation
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      alert(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    try {
      setIsLoading(true);
      const result = await updatePassword(password, newPassword);
      if (result.success) {
        updateLoginStatus(false);
        alert(result.message);
        navigate('/login');
      } else {
        alert(`Failed to update password: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      alert('An error occurred while updating password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        const result = await deleteAccount();
        if (result.success) {
          updateLoginStatus(false);
          alert('Your account has been successfully deleted.');
          navigate('/');
        } else {
          alert(`Failed to delete account: ${result.message}`);
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('An error occurred while trying to delete your account.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscriptionToggle = async () => {
    try {
      setIsLoading(true);
      if (isSubscribed) {
        const result = await unsubscribeFromNewsletter(email);
        if (result.success) {
          setIsSubscribed(false);
          alert('Successfully unsubscribed from newsletter.');
        } else {
          alert(`Failed to unsubscribe: ${result.message}`);
        }
      } else {
        const result = await subscribeToNewsletter(email);
        if (result.success) {
          setIsSubscribed(true);
          alert('Successfully subscribed to newsletter.');
        } else {
          alert(`Failed to subscribe: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      alert('An error occurred while updating subscription status.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    // Client-side email validation
    const emailError = validateEmail(newEmail);
    if (emailError) {
      alert(emailError);
      return;
    }

    try {
      setIsLoading(true);
      const result = await updateEmail(newEmail);
      if (result.success) {
        updateLoginStatus(false);
        alert(result.message);
        navigate('/login');
      } else {
        alert(`Failed to update email: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating email:', error);
      alert('An error occurred while updating email.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="section-heading">
        <h4>Account Dashboard</h4>
        <img src={require('../../assets/Images/heading-line-dec.png')} alt="" />
      </div>
      <div className="dashboard-content">
        <div className="dashboard-section">
          <h5>Change Email</h5>
          <input
            type="text"
            placeholder="New Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="off"
          />
          <button className="main-button" onClick={handleUpdateEmail}>Update Email</button>
        </div>

        <div className="dashboard-section">
          <h5>Change Password</h5>
          <input
            type="password"
            placeholder="Current Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
          />
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="off"
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="off"
          />
          <button className="main-button" onClick={handleUpdatePassword}>Update Password</button>
        </div>

        <div className="dashboard-section">
          <h5>Newsletter Preferences</h5>
          <button className="main-button" onClick={handleSubscriptionToggle}>
            {isSubscribed ? 'Unsubscribe from Newsletter' : 'Subscribe to Newsletter'}
          </button>
        </div>
        <div className="dashboard-section">
          <h5>Account Management</h5>
          <button className="main-button delete-account" onClick={handleDeleteAccount}>Delete Account</button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
